/**
 * TanStack Query hooks for `conversion-timing-analysis`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	fetchBreakdown,
	fetchHistogram,
	ingestPair,
	conversionTimingAnalysisQueryKeys,
	type BreakdownResponse,
	type Dimension,
	type HistogramResponse,
	type IngestPairBody,
	type IngestPairResponse,
} from "./api";

export function useConversionTimingAnalysisHistogram(
	params: { since_ts?: number; until_ts?: number } = {},
): UseQueryResult<HistogramResponse, Error> {
	return useQuery({
		queryKey: conversionTimingAnalysisQueryKeys.histogram(
			params.since_ts,
			params.until_ts,
		),
		queryFn: () => fetchHistogram(params),
	});
}

export function useConversionTimingAnalysisBreakdown(
	params: { by: Dimension; since_ts?: number; until_ts?: number },
): UseQueryResult<BreakdownResponse, Error> {
	return useQuery({
		queryKey: conversionTimingAnalysisQueryKeys.breakdown(
			params.by,
			params.since_ts,
			params.until_ts,
		),
		queryFn: () => fetchBreakdown(params),
	});
}

export function useConversionTimingAnalysisIngest(): UseMutationResult<
	IngestPairResponse,
	Error,
	IngestPairBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ingestPair,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: conversionTimingAnalysisQueryKeys.all });
		},
	});
}
